import { ArrowUpDown, Download, FileSignature, MoreVertical, RotateCcw, Ban, Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { openDocument } from "@/features/documents/api";
import { downloadSignedPdf } from "@/features/esignatures/api";
import { ESignatureStatusBadge } from "@/features/esignatures/components/esignature-status-badge";
import { ResendDialog } from "@/features/esignatures/components/resend-dialog";
import { RevokeDialog } from "@/features/esignatures/components/revoke-dialog";
import { useClientESignatures } from "@/features/esignatures/hooks/use-client-esignatures";
import {
  ACTIONABLE_STATUSES,
  type ESignatureRequest,
} from "@/features/esignatures/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

type SortKey = "document" | "created" | "sentBy" | "status";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Compact per-recipient progress, e.g. "1 of 2 signed". */
function recipientSummary(req: ESignatureRequest): string {
  if (req.recipients.length === 1) return req.recipients[0]?.name || "1 recipient";
  const signed = req.recipients.filter((r) => r.status === "SIGNED").length;
  return `${signed} of ${req.recipients.length} signed`;
}

export default function ClientESignaturesPage() {
  const { client } = useActiveClient();
  const { requests, loading, error, refetch } = useClientESignatures(client.id);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "created",
    dir: "desc",
  });
  const [resendTarget, setResendTarget] = useState<ESignatureRequest | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ESignatureRequest | null>(null);

  const sorted = useMemo(() => {
    const rows = [...requests];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (sort.key === "document") cmp = a.documentName.localeCompare(b.documentName);
      else if (sort.key === "created") cmp = a.createdAt.localeCompare(b.createdAt);
      else if (sort.key === "sentBy") cmp = a.sentByName.localeCompare(b.sentByName);
      else cmp = a.status.localeCompare(b.status);
      return cmp * dir;
    });
    return rows;
  }, [requests, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );

  const handleOpenSigned = (req: ESignatureRequest) => {
    downloadSignedPdf(req.id, `${req.documentName} (signed).pdf`).catch(() =>
      toast.error("Couldn't download the signed document."),
    );
  };

  const handleOpenOriginal = (req: ESignatureRequest) => {
    openDocument(req.document).catch(() =>
      toast.error("Couldn't open the document."),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 flex items-center gap-2 text-3xl font-bold">
          <FileSignature className="text-primary size-7" /> E-Signatures
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Track documents sent to this client for signature. Send a new request
          from the <Link to={`/client/${client.id}/documents`} className="text-primary hover:underline">Documents</Link> tab.
        </p>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-destructive p-6 text-sm">{error}</p>
        ) : sorted.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">
            No signature requests for this client yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead label="Document" active={sort.key === "document"} dir={sort.dir} onClick={() => toggleSort("document")} />
                <TableHead>Sign-by / recipient</TableHead>
                <SortHead label="Created" active={sort.key === "created"} dir={sort.dir} onClick={() => toggleSort("created")} />
                <SortHead label="By" active={sort.key === "sentBy"} dir={sort.dir} onClick={() => toggleSort("sentBy")} />
                <SortHead label="Status" active={sort.key === "status"} dir={sort.dir} onClick={() => toggleSort("status")} />
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((req) => {
                const canAct = ACTIONABLE_STATUSES.includes(req.status);
                return (
                  <TableRow key={req.id}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/client/${client.id}/esignatures/${req.id}`}
                        className="text-foreground hover:text-primary hover:underline"
                      >
                        {req.documentName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <div>{recipientSummary(req)}</div>
                      {req.signByDate && (
                        <div className="text-xs">by {formatDate(req.signByDate)}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(req.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {req.sentByName || "—"}
                    </TableCell>
                    <TableCell>
                      <ESignatureStatusBadge
                        status={req.status}
                        label={req.statusDisplay}
                        title={
                          req.status === "REVOKED" && req.voidedAt
                            ? `Revoked ${formatDate(req.voidedAt)}`
                            : req.statusDisplay
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${req.documentName}`}>
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/client/${client.id}/esignatures/${req.id}`}>
                              <Eye className="size-4" /> View details
                            </Link>
                          </DropdownMenuItem>
                          {req.status === "COMPLETED" && (
                            <DropdownMenuItem onClick={() => handleOpenSigned(req)}>
                              <Download className="size-4" /> Open signed document
                            </DropdownMenuItem>
                          )}
                          {req.status !== "COMPLETED" && (
                            <DropdownMenuItem onClick={() => handleOpenOriginal(req)}>
                              <Download className="size-4" /> Download original
                            </DropdownMenuItem>
                          )}
                          {canAct && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setResendTarget(req)}>
                                <RotateCcw className="size-4" /> Resend
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setRevokeTarget(req)}
                              >
                                <Ban className="size-4" /> Revoke
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <ResendDialog
        request={resendTarget}
        onOpenChange={(open) => !open && setResendTarget(null)}
        onResent={refetch}
      />
      <RevokeDialog
        request={revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        onRevoked={refetch}
      />
    </div>
  );
}

function SortHead({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <TableHead>
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1 font-semibold"
      >
        {label}
        <ArrowUpDown className={cn("size-3", active ? "opacity-100" : "opacity-40")} />
        {active ? (dir === "asc" ? "↑" : "↓") : ""}
      </button>
    </TableHead>
  );
}
